import type { ColumnDef } from '@tanstack/react-table'
import { ArrowRight } from 'lucide-react'
import type { StockOpportunity } from '@/lib/api-client'
import { formatCurrency } from '@/lib/utils'

interface Opts {
  demo: boolean
  mask: (skuId: number, name: string) => string
}

export function getOpportunityColumns({ demo, mask }: Opts): ColumnDef<StockOpportunity>[] {
  const cols: ColumnDef<StockOpportunity>[] = [
    {
      accessorKey: 'sku',
      header: 'SKU',
      cell: ({ row }) => (
        <span className="font-medium text-text-dark">{mask(row.original.skuId, row.original.sku)}</span>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <span className="text-[11px] uppercase tracking-wide text-text-muted">{row.original.type}</span>
      ),
    },
    {
      accessorKey: 'ratio',
      header: 'Imbalance',
      cell: ({ row }) => {
        const r = row.original.ratio
        const label = r == null ? '∞' : `${r}×`
        const strong = r == null || r >= 5
        return (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-bold ${
              strong ? 'bg-destructive/10 text-destructive' : 'bg-warning/10 text-warning'
            }`}
          >
            {label}
          </span>
        )
      },
    },
    {
      id: 'fast',
      header: 'High demand',
      cell: ({ row }) => (
        <span className="text-[13px]">
          {row.original.fastBranch} <span className="text-text-muted">({row.original.fastQty})</span>
        </span>
      ),
    },
    {
      id: 'slow',
      header: 'Low demand',
      cell: ({ row }) => (
        <span className="text-[13px]">
          {row.original.slowBranch} <span className="text-text-muted">({row.original.slowQty})</span>
        </span>
      ),
    },
    {
      id: 'transfer',
      header: 'Suggested move *',
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1.5 text-[13px] text-info">
          <span className="font-medium">{row.original.estTransferQty}u</span>
          <span className="text-text-muted">{row.original.fromBranch}</span>
          <ArrowRight className="w-3.5 h-3.5" />
          <span className="text-text-muted">{row.original.toBranch}</span>
        </span>
      ),
    },
  ]
  if (!demo) {
    cols.push({
      accessorKey: 'revenue',
      header: 'Rev (90d)',
      cell: ({ row }) => <span className="text-[13px] text-success">{formatCurrency(row.original.revenue)}</span>,
    })
  }
  return cols
}
